<template>
  <Dialog
    :model-value="modelValue"
    size="fullscreen"
    title="Analyse du besoin"
    :subtitle="sousTitre"
    flush
    :guard="dirty"
    data-testid="besoin-modal"
    @update:model-value="$emit('update:modelValue', $event)"
    @close="askClose"
    @close-blocked="abandonAsked = true"
  >
    <template #title-extra>
      <BesoinIntroHint />
    </template>

    <BesoinTunnelBody
      ref="bodyRef"
      :form="form"
      :initial-section="initialSection"
    />

    <!-- CE QUI MANQUE EST DIT AVANT LE CLIC, à côté du bouton qui refuse. La v1
         réservait ce message à un toast déclenché au clic — sur un bouton
         désactivé, donc jamais atteint. -->
    <template #footer-start>
      <FormFooterHint
        :message="blockingMessage"
        :ready="Boolean(quote) && !isQuoteSigned"
        ready-text="Tout est confirmé — le devis peut être édité."
        testid-prefix="besoin"
        @go="bodyRef?.goToFirstBlocking()"
      />
    </template>

    <template #footer>
      <Button variant="secondary" :disabled="busy" @click="askClose">
        {{ isQuoteSigned ? 'Fermer' : 'Annuler' }}
      </Button>
      <Button
        v-if="!isQuoteSigned"
        variant="primary"
        icon="fas fa-check"
        :loading="busy"
        :disabled="!canValider"
        data-testid="besoin-valider"
        @click="$emit('submit')"
      >
        {{ busy ? 'Enregistrement…' : 'Enregistrer et éditer le devis' }}
      </Button>
    </template>

    <template #overlay>
      <FormAbandonGuard
        :open="abandonAsked"
        title="Abandonner l’analyse en cours ?"
        text="Vous avez commencé à remplir l’analyse du besoin sans l’enregistrer. Fermer maintenant la perdra."
        stay-label="Continuer l’analyse"
        leave-label="Fermer sans enregistrer"
        testid-prefix="besoin"
        @continue="abandonAsked = false"
        @abandon="confirmAbandon"
      />
    </template>
  </Dialog>
</template>

<script setup>
/**
 * FENÊTRE « ANALYSE DU BESOIN » — nouvelle interface.
 *
 * Montée sur le kit v2 (`Dialog`, cf. docs/MODALES-V2.md M1) et rangée derrière
 * un PLAN. Ce qu'elle apporte, et que la version précédente n'avait pas :
 *
 *  — UN PLAN. Douze sections s'empilaient dans un rouleau unique : 6 972 px de
 *    contenu pour 606 px visibles sur le dossier de démonstration complet, soit
 *    douze écrans de défilement. Le commercial lit cet écran PENDANT son appel.
 *  — OÙ J'EN SUIS. Dix sections se confirment une par une ; le décompte de ce
 *    qui restait n'existait qu'en douzième section, donc après les douze écrans.
 *  — POURQUOI LE BOUTON REFUSE, dit à côté de lui et cliquable pour aller droit
 *    à la section concernée (`blockingMessage`).
 *  — LE BOUTON « CONFIRMER · SUIVANT » À DEMEURE sous le panneau : sur une
 *    section de 895 px, il fallait descendre pour l'atteindre, puis remonter.
 *  — L'IDENTIFICATION de la demande et du client, dans le sous-titre (M4).
 *  — LA GARDE de fermeture (M5, M21) : un questionnaire d'appel ne se jette plus
 *    sur un Échap.
 *  — L'ÉCHEC D'ENREGISTREMENT DANS LA FENÊTRE, la saisie intacte (M9).
 *
 * `Dialog` nu et non `DialogForm` : la fenêtre porte sa propre mise en page
 * (plan + panneau), ce que M2 réserve justement au tunnel. Elle reprend donc à
 * la main les deux services que `DialogForm` rend aux fenêtres ordinaires — le
 * bandeau d'erreur (porté par le corps) et la garde d'abandon.
 *
 * ELLE NE REND PLUS LE PLAN NI LES SECTIONS : c'est `BesoinTunnelBody`, qu'elle
 * partage avec la page pleine page (views/QuoteBesoinTunnel.vue). Il ne reste
 * ici que ce qui appartient à la FENÊTRE — le cadre, le sous-titre, le pied et
 * la garde de fermeture.
 *
 * ELLE EST PRÉSENTATIONNELLE (M10) : ni appel réseau ni toast ici. Tout l'état
 * vient de `useQuoteBesoinForm`, monté par `QuoteBesoinModal.vue`.
 *
 * PAS DE MODE APERÇU ICI : la route de test /formations/questionnaires-qualif/
 * apercu-tunnel montre le tunnel PLEINE PAGE, servi par la vue du même nom.
 * C'est elle qui passe `preview` au corps ; la fenêtre, jamais.
 */
import { computed, ref, watch } from 'vue'
import { Dialog } from '../ui/dialog'
import { Button } from '../ui'
import BesoinTunnelBody from './BesoinTunnelBody.vue'
import BesoinIntroHint from './BesoinIntroHint.vue'
import { FormFooterHint, FormAbandonGuard } from '../ui/form'
import { useDialogGuard } from '../../composables/ui/useDialogGuard.js'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  /** Tout l'état de la fenêtre, tel que `useQuoteBesoinForm` le rend. */
  form: { type: Object, required: true }
})

const emit = defineEmits(['update:modelValue', 'submit'])

// L'état du composable, déballé pour ce qui reste au cadre : le sous-titre, le
// pied, la garde. Le plan et les sections, eux, sont l'affaire du corps.
const {
  quote, loading, editedQuote, isQuoteSigned,
  nombreGroupes, besoinQuestionnaire, vigilanceComment, isEmailConfirmed,
  sectionsConfirmed, canValider, busy, blockingMessage
} = props.form

const abandonAsked = ref(false)
const bodyRef = ref(null)
const initialSection = ref('personal')

const sousTitre = computed(() => {
  const q = quote.value
  if (!q) return null
  const parts = [q.prenom, q.nom ? String(q.nom).toUpperCase() : null].filter(Boolean)
  const clientName = parts.length ? parts.join(' ') : (q.professionnel_nom_etablissement || null)
  const reference = `Demande #${q.human_id || q.id}`
  const formation = q.formation_nom || null
  return [clientName, reference, formation].filter(Boolean).join(' — ')
})

// GARDE (M21). Les données ne sont pas prêtes à l'ouverture : le point de
// référence n'est pris qu'une fois le chargement terminé, sinon la fenêtre
// s'ouvrirait déjà « modifiée » et Échap demanderait confirmation pour rien.
const guardSnapshot = computed(() => ({
  edited: editedQuote.value,
  besoin: besoinQuestionnaire,
  groupes: nombreGroupes.value,
  vigilance: vigilanceComment.value,
  sections: { ...sectionsConfirmed },
  emailConfirme: isEmailConfirmed.value
}))
const { dirty, baseline } = useDialogGuard(guardSnapshot, { immediate: false })

// Chaque ouverture repart de la première section et repose la garde.
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      initialSection.value = 'personal'
      bodyRef.value?.goTo('personal')
      abandonAsked.value = false
    }
  }
)

watch(loading, (isLoading, was) => {
  if (was && !isLoading) baseline()
})

function askClose() {
  if (dirty.value) {
    abandonAsked.value = true
    return
  }
  emit('update:modelValue', false)
}

function confirmAbandon() {
  abandonAsked.value = false
  emit('update:modelValue', false)
}

defineExpose({ baseline })
</script>
